import { FleetAreaCode, PermissionCode } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { useParams } from 'react-router';

import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { FleetVehicleDeadlinesTab } from '@/products/fleet/features/vehicle/fleet-vehicle-deadlines-tab';
import { FleetVehicleDetailStrip } from '@/products/fleet/features/vehicle/fleet-vehicle-detail-strip';
import { FleetVehicleDocumentsTab } from '@/products/fleet/features/vehicle/fleet-vehicle-documents-tab';
import { FleetVehicleDriversTab } from '@/products/fleet/features/vehicle/fleet-vehicle-drivers-tab';
import { FleetVehicleOdometerTab } from '@/products/fleet/features/vehicle/fleet-vehicle-odometer-tab';
import { FleetVehicleStammTab } from '@/products/fleet/features/vehicle/fleet-vehicle-stamm-tab';
import {
  FleetVehicleTabNav,
  type FleetVehicleTabId,
} from '@/products/fleet/features/vehicle/fleet-vehicle-tab-nav';
import { FleetVehicleDamagesTab } from '@/products/fleet/features/vehicle/fleet-vehicle-damages-tab';
import { FleetVehicleWorkshopTab } from '@/products/fleet/features/vehicle/fleet-vehicle-workshop-tab';
import { FleetVehicleTripsTab } from '@/products/fleet/features/vehicle/fleet-vehicle-trips-tab';
import { FleetVehicleCostsTab } from '@/products/fleet/features/vehicle/fleet-vehicle-costs-tab';
import { fleetAreaOn, useFleetAreas } from '@/products/fleet/features/use-fleet-areas';
import { useFleetVehicle } from '@/products/fleet/features/use-fleet';

export function FleetVehicleDetailPage() {
  const { id = '' } = useParams();
  const vehicle = useFleetVehicle(id);
  usePageTitle(vehicle.data?.licensePlate ?? 'Fahrzeug');
  const me = useMe();
  const areas = useFleetAreas();
  const catalog = areas.isError ? undefined : areas.data;
  const manage = hasPermission(me.data, PermissionCode.FLEET_VEHICLES_MANAGE);
  const assignManage = hasPermission(me.data, PermissionCode.FLEET_ASSIGNMENTS_MANAGE);
  const workshopManage = hasPermission(me.data, PermissionCode.FLEET_WORKSHOP_MANAGE);
  const documentsManage = hasPermission(me.data, PermissionCode.FLEET_DOCUMENTS_MANAGE);
  const telematicsRead = hasPermission(me.data, PermissionCode.FLEET_TELEMATICS_READ);
  const tripsOwn = hasPermission(me.data, PermissionCode.FLEET_TRIPS_READ_OWN);
  const telematicsManage = hasPermission(me.data, PermissionCode.FLEET_TELEMATICS_MANAGE);
  const fuelRead = hasPermission(me.data, PermissionCode.FLEET_FUEL_READ);
  const showDeadlines = fleetAreaOn(catalog, FleetAreaCode.DEADLINES);
  const showMileage = fleetAreaOn(catalog, FleetAreaCode.MILEAGE);
  const showDamage = fleetAreaOn(catalog, FleetAreaCode.DAMAGE);
  const showWorkshop = workshopManage && fleetAreaOn(catalog, FleetAreaCode.WORKSHOP);
  const showTrips = (telematicsRead || tripsOwn) && fleetAreaOn(catalog, FleetAreaCode.TRIPS);
  const showCosts =
    (fuelRead && fleetAreaOn(catalog, FleetAreaCode.FUEL)) ||
    (workshopManage && fleetAreaOn(catalog, FleetAreaCode.WORKSHOP));
  const [tab, setTab] = useState<FleetVehicleTabId>('stamm');
  const activeTab =
    (tab === 'fristen' && !showDeadlines) ||
    (tab === 'kilometer' && !showMileage) ||
    (tab === 'schaeden' && !showDamage) ||
    (tab === 'werkstatt' && !showWorkshop) ||
    (tab === 'fahrten' && !showTrips) ||
    (tab === 'kosten' && !showCosts)
      ? 'stamm'
      : tab;

  return (
    <div className="space-y-6">
      {vehicle.isPending ? (
        <p className="text-sm text-muted-foreground">Fahrzeug wird geladen …</p>
      ) : null}
      {vehicle.isError ? (
        <p className="text-sm text-destructive">
          {vehicle.error instanceof ApiRequestError
            ? vehicle.error.message
            : 'Das Fahrzeug konnte nicht geladen werden.'}
        </p>
      ) : null}
      {vehicle.data ? (
        <>
          <FleetVehicleDetailStrip vehicle={vehicle.data} />
          <FleetVehicleTabNav
            tab={activeTab}
            onTab={setTab}
            showDeadlines={showDeadlines}
            showMileage={showMileage}
            showDamage={showDamage}
            showWorkshop={showWorkshop}
            showTrips={showTrips}
            showCosts={showCosts}
          />
          {activeTab === 'stamm' ? (
            <FleetVehicleStammTab
              vehicle={vehicle.data}
              manage={manage}
              telematicsManage={telematicsManage}
            />
          ) : null}
          {activeTab === 'fahrer' ? (
            <FleetVehicleDriversTab vehicleId={id} manage={assignManage} selfId={me.data?.id} />
          ) : null}
          {activeTab === 'fristen' && showDeadlines ? (
            <FleetVehicleDeadlinesTab vehicleId={id} manage={manage} />
          ) : null}
          {activeTab === 'kilometer' && showMileage ? (
            <FleetVehicleOdometerTab vehicleId={id} />
          ) : null}
          {activeTab === 'schaeden' && showDamage ? (
            <FleetVehicleDamagesTab vehicle={vehicle.data} manage={manage} />
          ) : null}
          {activeTab === 'werkstatt' && showWorkshop ? (
            <FleetVehicleWorkshopTab vehicleId={id} />
          ) : null}
          {activeTab === 'dokumente' ? (
            <FleetVehicleDocumentsTab vehicleId={id} canUpload={documentsManage} />
          ) : null}
          {activeTab === 'fahrten' && showTrips ? <FleetVehicleTripsTab vehicleId={id} /> : null}
          {activeTab === 'kosten' && showCosts ? <FleetVehicleCostsTab vehicleId={id} /> : null}
        </>
      ) : null}
    </div>
  );
}
